import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Globe2, Gift, Gamepad2, MessageCircle, Trophy, Shield, Sparkles, Users, Rocket, Heart, Crown, X, Play, Mail, Lock, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import InstallPwaButton from '../components/InstallPwaButton';
import FounderWall from '../components/FounderWall';
import { ALL_RANK_MEDALS } from '../components/RankMedal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PLANETS, COSMIC_RANKS } from '../mock/mock';
import { useAuth } from '../context/AuthContext';
import AppleLoginButton from '../components/AppleLoginButton';
import api from '../lib/api';
import { toast } from 'sonner';

const HERO_IMG = '/hero-worldvibe.jpg';

const FEATURES = [
  { icon: Globe2, title: 'Join Planets', desc: 'Discover thousands of communities around your interests, language and culture.', color: 'from-fuchsia-500 to-purple-600' },
  { icon: MessageCircle, title: 'Real-Time Chat', desc: 'Public rooms, private DMs and group chats with travelers from around the world.', color: 'from-cyan-500 to-blue-600' },
  { icon: Gift, title: 'Send Gifts', desc: 'Send roses, diamonds, rockets and entire galaxies to people you vibe with.', color: 'from-pink-500 to-rose-600' },
  { icon: Gamepad2, title: 'Play & Earn', desc: 'Mini-games, lotteries and quests that reward Stars, XP and rare cosmetics.', color: 'from-emerald-500 to-teal-600' },
  { icon: Trophy, title: 'Cosmic Ranks', desc: 'Climb 12 cosmic levels from Explorer to Supreme Being. Unlock prestige.', color: 'from-amber-500 to-orange-600' },
  { icon: Shield, title: 'Safe Space', desc: 'Strong moderation by Guardians. No tolerance for harassment.', color: 'from-indigo-500 to-violet-600' },
];

export default function Landing() {
  const [trailerOpen, setTrailerOpen] = useState(false);
  return (    <div className="min-h-screen cosmic-bg relative overflow-hidden text-white">
      <div className="stars-bg" />

      {/* Nav */}
      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo size="lg" showText animate />
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#planets" className="hover:text-white">Planets</a>
            <a href="#ranks" className="hover:text-white">Ranks</a>
            <a href="#download" className="hover:text-white">Download</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="#quick-login" className="hidden sm:inline-flex items-center text-sm text-white/70 hover:text-white">Sign in</a>
            <Link to="/app">
              <Button className="bg-white text-black hover:bg-white/90 font-semibold rounded-full px-5">Enter App<ArrowRight size={16} className="ml-1" /></Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-12 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            {/* Meaningful live badge — replaces the old "Early Access" chip
                with something a first-time visitor can actually use to
                decide "is this app for me". Three concrete promises. */}
            <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 px-3.5 py-1.5 rounded-full text-[11px] glass" data-testid="hero-badge">
              <span className="text-white/90 font-semibold uppercase tracking-wider inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live now
              </span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">Free forever</span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">No ads on chat</span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">30-second signup</span>
            </div>
            <h1 className="font-display font-bold text-4xl md:text-6xl lg:text-6xl mt-6 leading-[1.08]">
              Explore the <span className="text-gradient">world</span>,<br />one conversation at a time.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              WorldVibe is a global social platform where you <span className="text-white">join themed communities</span>, <span className="text-white">chat in real time</span>, <span className="text-white">play mini-games</span>, and <span className="text-white">meet new people</span> — all in one cosmic space.
            </p>
            <p className="mt-3 text-sm text-white/50 max-w-xl">
              Real people. Real conversations. Real communities across the globe.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button size="lg" data-testid="hero-signup-cta" className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white font-semibold rounded-full px-7 h-12 border-0 shadow-lg shadow-fuchsia-500/30">
                  <Rocket size={18} className="mr-2" /> Create your account
                </Button>
              </Link>
              <Button
                onClick={() => setTrailerOpen(true)}
                variant="outline"
                size="lg"
                data-testid="watch-trailer-btn"
                className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10 text-white"
              >
                <Sparkles size={18} className="mr-2" /> Watch trailer
              </Button>
            </div>

            {/* Inline quick login — returning travelers sign in right here
                without navigating away. The prominent sign-in surface on the
                page (standalone Sign-in button removed to avoid duplication). */}
            <QuickLoginForm />
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative aspect-square max-w-[520px] mx-auto">
              <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle, rgba(123,47,247,0.35) 0%, rgba(0,198,255,0.15) 50%, transparent 70%)' }} />
              <div className="absolute inset-8 rounded-full border border-cyan-400/20 orbit-spin" />
              <div className="absolute inset-16 rounded-full border border-fuchsia-500/20 orbit-spin-reverse" />
              <div className="absolute inset-24 rounded-full border border-amber-400/20 orbit-spin" />

              {/* Cinematic welcome hero — Gemini Nano Banana rendered
                  cosmic-travelers scene. Portrait shape balances the
                  copy on the left; softened corners + glow keep it
                  on-brand with the rest of the app. */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-3xl blur-3xl opacity-60" style={{ background: 'conic-gradient(from 0deg, #7B2FF7, #00C6FF, #00F5D4, #FB923C, #7B2FF7)', animation: 'aurora-spin 8s linear infinite' }} />
                  <img
                    src={HERO_IMG}
                    alt="WorldVibe — cosmic travelers gathered under a bright nebula, chat bubbles floating around them"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    data-testid="landing-hero-image"
                    className="relative w-[22rem] md:w-[30rem] lg:w-[34rem] aspect-video rounded-3xl object-cover shadow-2xl shadow-fuchsia-500/30 hero-float border border-white/10"
                  />
                  <div className="absolute -inset-2 rounded-3xl ring-2 ring-cyan-400/25 pointer-events-none" />
                </div>
              </div>

              {/* Floating cards */}
              <FloatingCard className="top-4 -left-2" icon={<Heart className="text-pink-400" />} title="NebulaQueen" sub="sent you a 💎 Diamond" delay={0} />
              <FloatingCard className="bottom-12 -left-4" icon={<Globe2 className="text-cyan-400" />} title="Aurora Lounge" sub="Chill vibes · Music" delay={0.6} />
              <FloatingCard className="top-12 -right-4" icon={<Trophy className="text-amber-400" />} title="Rank Up!" sub="You reached Cosmonaut" delay={1.2} />
              <FloatingCard className="bottom-4 -right-2" icon={<Crown className="text-fuchsia-400" />} title="Daily Reward" sub="+250 stars collected" delay={1.8} />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Wall — social proof: every launch traveler on parade */}
      <FounderWall />

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">The Universe</div>
          <h2 className="font-display font-bold text-4xl md:text-5xl">A whole world inside your pocket</h2>
          <p className="mt-4 text-white/60">Six pillars of cosmic interaction designed to make every connection feel alive.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, idx) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="group relative rounded-2xl p-6 glass cosmic-border tile-rise overflow-hidden hover:-translate-y-1" style={{ animationDelay: `${idx * 80}ms` }}>
                <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none" style={{ background: `linear-gradient(135deg, rgba(123,47,247,0.15), rgba(0,198,255,0.12), rgba(0,245,212,0.1))`, transition: 'opacity 300ms ease' }} />
                <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-transform`}>
                  <Icon className="text-white" size={22} />
                </div>
                <h3 className="relative font-display font-semibold text-xl">{f.title}</h3>
                <p className="relative mt-2 text-sm text-white/60 leading-relaxed">{f.desc}</p>
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 300ms ease' }} />
              </div>
            );
          })}
        </div>
      </section>

      {/* Planets Showcase */}
      <section id="planets" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-400 mb-3">Discover</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl">Featured Planets</h2>
          </div>
          <Link to="/app/planets" className="text-sm text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">Browse all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANETS.slice(0, 4).map((p, idx) => (
            <Link to={`/app/planets/${p.id}`} key={p.id} className="group relative rounded-2xl overflow-hidden glass hover:scale-[1.04] hover:-translate-y-1 tile-rise" style={{ animationDelay: `${idx * 90}ms` }}>
              <div className={`aspect-[5/4] bg-gradient-to-br ${p.color} relative overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center text-7xl opacity-90 group-hover:scale-125 group-hover:rotate-6 transition-transform" style={{ transitionDuration: '500ms' }}>{p.emoji}</div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2), transparent 60%)', transition: 'opacity 300ms ease' }} />
                <div className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-black/30 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-cyan-300 rounded-full animate-pulse" /> Now open
                </div>
              </div>
              <div className="p-4 relative">
                <div className="font-display font-semibold">{p.name}</div>
                <div className="text-xs text-white/60 mt-1 line-clamp-2">{p.description}</div>
                <div className="text-xs text-white/50 mt-3 flex items-center gap-1"><Users size={12} /> Join the founding travelers</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Ranks Preview */}
      <section id="ranks" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">Cosmic Rank System</div>
          <h2 className="font-display font-bold text-4xl md:text-5xl">From New Vibe to Zenith Vibe</h2>
          <p className="mt-4 text-white/60">Earn XP through chat, gifts, games and missions to climb 12 legendary cosmic ranks — each with its own signature medal.</p>
        </div>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-5">
          {ALL_RANK_MEDALS.map((m, idx) => {
            const cosmicName = COSMIC_RANKS.find(r => r.rank === m.rank)?.name || '';
            return (
              <div
                key={m.rank}
                data-testid={`landing-medal-${m.rank}`}
                className="relative rounded-2xl p-4 flex flex-col items-center gap-2.5 border tile-rise transition hover:-translate-y-1"
                style={{
                  animationDelay: `${idx * 55}ms`,
                  borderColor: m.color + '55',
                  background: `linear-gradient(180deg, ${m.color}15 0%, rgba(255,255,255,0.02) 100%)`,
                  boxShadow: `0 0 24px ${m.color}22`,
                }}
              >
                {/* Rank chip */}
                <span
                  className="absolute -top-2 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: m.color, color: '#0A0A0F' }}
                >
                  Rank {m.rank}
                </span>
                {/* Medal artwork — big and centred */}
                <div
                  className="relative w-full aspect-square rounded-xl overflow-hidden flex items-center justify-center"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${m.accent}22 0%, transparent 65%), #0a0a15` }}
                >
                  <img
                    src={m.src}
                    alt={m.name}
                    loading="lazy"
                    className="w-[86%] h-[86%] object-contain"
                    style={{ filter: `drop-shadow(0 0 12px ${m.accent}88)` }}
                  />
                </div>
                {/* Name + cosmic rank subtext */}
                <div className="text-center">
                  <div className="text-sm font-display font-bold leading-tight" style={{ color: m.color }}>
                    {m.name}
                  </div>
                  {cosmicName && (
                    <div className="text-[11px] text-white/45 mt-0.5">{cosmicName}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section id="download" className="relative z-10 max-w-5xl mx-auto px-6 py-20">
        <div className="relative rounded-3xl p-10 md:p-14 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(123,47,247,0.2), rgba(0,198,255,0.18), rgba(0,245,212,0.12))' }}>
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-50" style={{ background: '#7B2FF7' }} />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full blur-3xl opacity-40" style={{ background: '#00C6FF' }} />
          <div className="relative">
            <h2 className="font-display font-bold text-3xl md:text-5xl max-w-2xl">Your universe is waiting.<br />Step inside.</h2>
            <p className="mt-4 text-white/70 max-w-xl">No ads. No phone number needed. Just real people from every corner of the world.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/app"><Button size="lg" className="bg-white text-black hover:bg-white/90 rounded-full px-7 h-12 font-semibold"><Rocket size={18} className="mr-2" />Launch WorldVibe</Button></Link>
              <a
                href="https://play.google.com/store/apps/details?id=com.worldvibe.app"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-google-play-btn"
              >
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10 text-white">Get on Google Play</Button>
              </a>
              <a
                href="https://apps.apple.com/app/id6795453750"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-app-store-btn"
              >
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10 text-white">Get on App Store</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="text-white/40 text-sm">· Meet the World in Real Time</span>
          </div>
          <div className="text-xs text-white/40">Powered by Sarvaritam Businesses LLP · © 2026 WorldVibe</div>
          <div className="flex gap-5 text-sm text-white/50">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-link-terms">Terms</Link>
            <Link to="/child-safety" className="hover:text-white" data-testid="footer-link-child-safety">Child Safety</Link>
            <Link to="/delete-account" className="hover:text-white" data-testid="footer-link-delete-account">Delete Account</Link>
            <Link to="/guidelines" className="hover:text-white" data-testid="footer-link-guidelines">Guidelines</Link>
            <Link to="/contact" className="hover:text-white" data-testid="footer-link-contact">Contact us</Link>
          </div>
        </div>
      </footer>

      {trailerOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setTrailerOpen(false)}
          data-testid="trailer-modal"
          role="dialog"
          aria-modal="true"
          aria-label="WorldVibe trailer"
        >
          <button
            onClick={() => setTrailerOpen(false)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            data-testid="trailer-close-btn"
            aria-label="Close trailer"
          >
            <X size={18} />
          </button>
          <div
            className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src="/trailer.mp4"
              controls
              autoPlay
              playsInline
              className="w-full h-full bg-black"
              data-testid="trailer-video"
            >
              <track kind="captions" />
              Sorry, your browser does not support embedded videos.
            </video>
          </div>
        </div>
      )}
      <InstallPwaButton />
    </div>
  );
}

function FloatingCard({ className, icon, title, sub, delay = 0 }) {
  return (
    <div className={`absolute glass rounded-xl p-3 flex items-center gap-3 min-w-[180px] cosmic-border float-slow ${className}`} style={{ animationDelay: `${delay}s` }}>
      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">{icon}</div>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{title}</div>
        <div className="text-[11px] text-white/60 truncate">{sub}</div>
      </div>
    </div>
  );
}

/* Quick sign-in right on the landing page — the primary sign-in surface.
   Prominent glass card with glowing border, email + password, plus real
   Google / Apple OAuth buttons. */
function QuickLoginForm() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) { toast.success('Welcome back ✨'); api.post('/planets/p5/join').catch(() => {}); nav('/app/planets/p5'); }
    else setError(res.error);
  };

  return (
    <form
      onSubmit={submit}
      id="quick-login"
      data-testid="quick-login-form"
      className="mt-7 max-w-xl rounded-2xl border border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 via-white/[0.05] to-fuchsia-500/10 backdrop-blur-md p-5 scroll-mt-24 shadow-[0_0_40px_rgba(34,211,238,0.15)]"
    >
      <div className="flex items-center gap-2 mb-3.5">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-sm font-display font-bold text-white tracking-wide">
          Already a traveler? <span className="text-gradient">Sign in here</span>
        </span>
      </div>

      {/* OAuth — same flows as /login */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={loginWithGoogle}
          data-testid="quick-login-google"
          className="flex-1 h-11 rounded-xl bg-white text-black font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-white/90 transition"
        >
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83Z"/><path fill="#EA4335" d="M12 5.38c1.61 0 3.06.55 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
          Google
        </button>
        <div className="flex-1 [&>button]:!mt-0 [&>button]:!h-11 [&>button]:!rounded-xl [&>button]:!text-sm">
          <AppleLoginButton label="Apple" next="/app/planets/p5" />
        </div>
      </div>

      <div className="flex items-center gap-3 my-3 text-[10px] text-white/40 uppercase tracking-wider">
        <div className="flex-1 h-px bg-white/10" /> or with email <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            placeholder="you@worldvibe.app"
            data-testid="quick-login-email"
            className="pl-8 h-11 bg-black/30 border-white/15 text-white placeholder:text-white/40 text-sm"
          />
        </div>
        <div className="relative flex-1">
          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
            placeholder="Password"
            data-testid="quick-login-password"
            className="pl-8 h-11 bg-black/30 border-white/15 text-white placeholder:text-white/40 text-sm"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="quick-login-submit"
          className="h-11 px-6 bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0 font-semibold rounded-xl shrink-0 shadow-lg shadow-fuchsia-500/25"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <>Sign in <ArrowRight size={14} className="ml-1" /></>}
        </Button>
      </div>
      <div className="mt-2.5 flex items-center justify-between text-[11px]">
        {error
          ? <span className="text-rose-400" data-testid="quick-login-error">{error}</span>
          : <span />}
        <Link to="/login" className="text-cyan-300 hover:text-cyan-200">Forgot password?</Link>
      </div>
    </form>
  );
}
